import { useState, useEffect, useRef, useCallback } from "react";
import {
  MousePointer,
  Pencil,
  ArrowRight,
  Square,
  Circle as CircleIcon,
  Minus,
  Highlighter,
  Type,
  Hash,
  EyeOff,
  RotateCcw,
  Copy,
  Download,
  Share2,
  X,
  Loader2,
  Crop,
} from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";

const COLOR_SWATCHES = [
  { name: "Rouge", hex: "#ef4444" },
  { name: "Jaune", hex: "#f59e0b" },
  { name: "Vert", hex: "#10b981" },
  { name: "Cyan", hex: "#06b6d4" },
  { name: "Bleu", hex: "#3b82f6" },
  { name: "Violet", hex: "#a855f7" },
  { name: "Blanc", hex: "#ffffff" },
  { name: "Noir", hex: "#000000" },
];

const STROKE_WIDTHS = [2, 4, 8];

export default function FloombySnipper({ isActive, onClose, containerRef, displayStreamRef }) {
  // Screen background frame canvas
  const [screenSnapshot, setScreenSnapshot] = useState(null);

  // Selection box state { x, y, w, h }
  const [selection, setSelection] = useState(null);
  const [dragMode, setDragMode] = useState(null); // 'create' | 'move' | handleName
  const [dragStart, setDragStart] = useState(null);
  const [initialSelection, setInitialSelection] = useState(null);

  // Active Tool & Style
  const [activeTool, setActiveTool] = useState("select"); // 'select'|'pen'|'arrow'|'rect'|'circle'|'line'|'highlighter'|'text'|'step'|'blur'
  const [activeColor, setActiveColor] = useState("#ef4444");
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [stepCounter, setStepCounter] = useState(1);

  // Annotations stack
  const [annotations, setAnnotations] = useState([]);
  const [currentAnnotation, setCurrentAnnotation] = useState(null);
  const [textInput, setTextInput] = useState(null); // { x, y, text }

  const [isProcessing, setIsProcessing] = useState(false);
  const containerOverlayRef = useRef(null);
  const annotationCanvasRef = useRef(null);

  // 1. Capture 100% real screen snapshot when activated
  const captureFullDesktop = useCallback(async () => {
    let capturedCanvas = null;

    try {
      // Strategy A: If displayStream (screen recording / share) active, grab frame from it
      if (displayStreamRef?.current && displayStreamRef.current.getVideoTracks().length > 0) {
        const videoTrack = displayStreamRef.current.getVideoTracks()[0];
        if (videoTrack && videoTrack.readyState === "live") {
          try {
            const video = document.createElement("video");
            video.muted = true;
            video.srcObject = new MediaStream([videoTrack]);
            await video.play();

            const c = document.createElement("canvas");
            c.width = video.videoWidth || window.innerWidth;
            c.height = video.videoHeight || window.innerHeight;
            const ctx = c.getContext("2d");
            ctx.drawImage(video, 0, 0, c.width, c.height);
            capturedCanvas = c;
          } catch {}
        }
      }

      // Strategy B: If no displayStream, fallback to html2canvas + composite videos
      if (!capturedCanvas) {
        try {
          const container = containerRef.current;
          if (container) {
            capturedCanvas = await html2canvas(container, {
              useCORS: true,
              allowTaint: true,
              backgroundColor: "#0f0f1a",
              scale: window.devicePixelRatio || 2,
              logging: false,
            });

            const cRect = container.getBoundingClientRect();
            const scaleX = capturedCanvas.width / cRect.width;
            const scaleY = capturedCanvas.height / cRect.height;
            const ctx = capturedCanvas.getContext("2d");
            const videos = container.querySelectorAll("video");
            videos.forEach((v) => {
              if (v.readyState >= 2 && v.videoWidth > 0) {
                try {
                  const vRect = v.getBoundingClientRect();
                  ctx.drawImage(v, (vRect.left - cRect.left) * scaleX, (vRect.top - cRect.top) * scaleY, vRect.width * scaleX, vRect.height * scaleY);
                } catch {}
              }
            });
          }
        } catch {}
      }

      setScreenSnapshot(capturedCanvas);
    } catch (e) {
      console.error("Floomby capture error:", e);
    }
  }, [containerRef, displayStreamRef]);

  // Reset when activated
  useEffect(() => {
    if (isActive) {
      setSelection(null);
      setAnnotations([]);
      setCurrentAnnotation(null);
      setTextInput(null);
      setStepCounter(1);
      setActiveTool("select");
      captureFullDesktop();
    } else {
      setScreenSnapshot(null);
    }
  }, [isActive, captureFullDesktop]);

  // ESC to close
  useEffect(() => {
    if (!isActive) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (textInput) setTextInput(null);
        else onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isActive, textInput, onClose]);

  // Redraw annotations on canvas whenever annotations change
  useEffect(() => {
    const canvas = annotationCanvasRef.current;
    if (!canvas || !selection) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const renderAnnotation = (ann) => {
      ctx.save();
      ctx.strokeStyle = ann.color;
      ctx.fillStyle = ann.color;
      ctx.lineWidth = ann.width;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      switch (ann.type) {
        case "pen":
          if (ann.points && ann.points.length > 0) {
            ctx.beginPath();
            ctx.moveTo(ann.points[0].x, ann.points[0].y);
            for (let i = 1; i < ann.points.length; i++) {
              ctx.lineTo(ann.points[i].x, ann.points[i].y);
            }
            ctx.stroke();
          }
          break;

        case "highlighter":
          if (ann.points && ann.points.length > 0) {
            ctx.save();
            ctx.globalAlpha = 0.35;
            ctx.lineWidth = ann.width * 3;
            ctx.beginPath();
            ctx.moveTo(ann.points[0].x, ann.points[0].y);
            for (let i = 1; i < ann.points.length; i++) {
              ctx.lineTo(ann.points[i].x, ann.points[i].y);
            }
            ctx.stroke();
            ctx.restore();
          }
          break;

        case "line":
          ctx.beginPath();
          ctx.moveTo(ann.startX, ann.startY);
          ctx.lineTo(ann.endX, ann.endY);
          ctx.stroke();
          break;

        case "arrow":
          const dx = ann.endX - ann.startX;
          const dy = ann.endY - ann.startY;
          const angle = Math.atan2(dy, dx);
          const headLen = Math.max(12, ann.width * 3);

          ctx.beginPath();
          ctx.moveTo(ann.startX, ann.startY);
          ctx.lineTo(ann.endX, ann.endY);
          ctx.stroke();

          ctx.beginPath();
          ctx.moveTo(ann.endX, ann.endY);
          ctx.lineTo(ann.endX - headLen * Math.cos(angle - Math.PI / 6), ann.endY - headLen * Math.sin(angle - Math.PI / 6));
          ctx.lineTo(ann.endX - headLen * Math.cos(angle + Math.PI / 6), ann.endY - headLen * Math.sin(angle + Math.PI / 6));
          ctx.closePath();
          ctx.fill();
          break;

        case "rect":
          const rx = Math.min(ann.startX, ann.endX);
          const ry = Math.min(ann.startY, ann.endY);
          const rw = Math.abs(ann.endX - ann.startX);
          const rh = Math.abs(ann.endY - ann.startY);
          ctx.strokeRect(rx, ry, rw, rh);
          break;

        case "circle":
          const cx = (ann.startX + ann.endX) / 2;
          const cy = (ann.startY + ann.endY) / 2;
          const radiusX = Math.abs(ann.endX - ann.startX) / 2;
          const radiusY = Math.abs(ann.endY - ann.startY) / 2;
          ctx.beginPath();
          ctx.ellipse(cx, cy, radiusX, radiusY, 0, 0, 2 * Math.PI);
          ctx.stroke();
          break;

        case "step":
          ctx.beginPath();
          ctx.arc(ann.x, ann.y, 14, 0, 2 * Math.PI);
          ctx.fill();
          ctx.fillStyle = "#ffffff";
          ctx.font = "bold 13px sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(ann.stepNum.toString(), ann.x, ann.y);
          break;

        case "text":
          ctx.font = `bold ${Math.max(14, ann.width * 4)}px sans-serif`;
          ctx.textAlign = "left";
          ctx.textBaseline = "top";
          ctx.fillText(ann.text, ann.x, ann.y);
          break;

        case "blur":
          const bx = Math.min(ann.startX, ann.endX);
          const by = Math.min(ann.startY, ann.endY);
          const bw = Math.abs(ann.endX - ann.startX);
          const bh = Math.abs(ann.endY - ann.startY);
          ctx.save();
          ctx.fillStyle = "rgba(15, 15, 26, 0.95)";
          ctx.fillRect(bx, by, bw, bh);
          ctx.restore();
          break;

        default:
          break;
      }

      ctx.restore();
    };

    allAnnotations.forEach(renderAnnotation);
  }, [annotations, currentAnnotation, selection]);

  const allAnnotations = currentAnnotation ? [...annotations, currentAnnotation] : annotations;

  // Helper for handles
  const getHandleAt = (x, y, sel) => {
    if (!sel) return null;
    const handles = [
      { name: "nw", x: sel.x, y: sel.y },
      { name: "ne", x: sel.x + sel.w, y: sel.y },
      { name: "se", x: sel.x + sel.w, y: sel.y + sel.h },
      { name: "sw", x: sel.x, y: sel.y + sel.h },
      { name: "n", x: sel.x + sel.w / 2, y: sel.y },
      { name: "e", x: sel.x + sel.w, y: sel.y + sel.h / 2 },
      { name: "s", x: sel.x + sel.w / 2, y: sel.y + sel.h },
      { name: "w", x: sel.x, y: sel.y + sel.h / 2 },
    ];
    for (const h of handles) {
      if (Math.abs(x - h.x) <= 10 && Math.abs(y - h.y) <= 10) return h.name;
    }
    return null;
  };

  // ── Mouse interactions ──
  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    const clientX = e.clientX;
    const clientY = e.clientY;

    if (textInput) {
      if (textInput.text.trim()) {
        setAnnotations((prev) => [
          ...prev,
          { type: "text", x: textInput.x, y: textInput.y, text: textInput.text, color: activeColor, width: strokeWidth },
        ]);
      }
      setTextInput(null);
      return;
    }

    if (activeTool === "step" && selection) {
      setAnnotations((prev) => [
        ...prev,
        { type: "step", x: clientX, y: clientY, stepNum: stepCounter, color: activeColor, width: strokeWidth },
      ]);
      setStepCounter((prev) => prev + 1);
      return;
    }

    if (activeTool === "text" && selection) {
      setTextInput({ x: clientX, y: clientY, text: "" });
      return;
    }

    if (activeTool !== "select" && selection) {
      setDragMode("draw");
      const newAnn = {
        type: activeTool,
        color: activeColor,
        width: strokeWidth,
        startX: clientX,
        startY: clientY,
        endX: clientX,
        endY: clientY,
        points: [{ x: clientX, y: clientY }],
      };
      setCurrentAnnotation(newAnn);
      return;
    }

    if (!selection) {
      setDragMode("create");
      setDragStart({ x: clientX, y: clientY });
      setSelection({ x: clientX, y: clientY, w: 0, h: 0 });
    } else {
      const handle = getHandleAt(clientX, clientY, selection);
      if (handle) {
        setDragMode(`handle-${handle}`);
        setDragStart({ x: clientX, y: clientY });
        setInitialSelection({ ...selection });
      } else if (
        clientX >= selection.x &&
        clientX <= selection.x + selection.w &&
        clientY >= selection.y &&
        clientY <= selection.y + selection.h
      ) {
        setDragMode("move");
        setDragStart({ x: clientX, y: clientY });
        setInitialSelection({ ...selection });
      } else {
        setDragMode("create");
        setDragStart({ x: clientX, y: clientY });
        setSelection({ x: clientX, y: clientY, w: 0, h: 0 });
      }
    }
  };

  const handleMouseMove = (e) => {
    if (!dragMode) return;
    const clientX = e.clientX;
    const clientY = e.clientY;

    if (dragMode === "draw" && currentAnnotation) {
      if (activeTool === "pen" || activeTool === "highlighter") {
        setCurrentAnnotation((prev) => ({
          ...prev,
          points: [...prev.points, { x: clientX, y: clientY }],
        }));
      } else {
        setCurrentAnnotation((prev) => ({
          ...prev,
          endX: clientX,
          endY: clientY,
        }));
      }
      return;
    }

    if (dragMode === "create" && dragStart) {
      const x = Math.min(dragStart.x, clientX);
      const y = Math.min(dragStart.y, clientY);
      const w = Math.abs(clientX - dragStart.x);
      const h = Math.abs(clientY - dragStart.y);
      setSelection({ x, y, w, h });
      return;
    }

    if (dragMode === "move" && dragStart && initialSelection) {
      const dx = clientX - dragStart.x;
      const dy = clientY - dragStart.y;
      setSelection({
        x: Math.max(0, Math.min(window.innerWidth - initialSelection.w, initialSelection.x + dx)),
        y: Math.max(0, Math.min(window.innerHeight - initialSelection.h, initialSelection.y + dy)),
        w: initialSelection.w,
        h: initialSelection.h,
      });
      return;
    }

    if (dragMode.startsWith("handle-") && dragStart && initialSelection) {
      const handle = dragMode.replace("handle-", "");
      const dx = clientX - dragStart.x;
      const dy = clientY - dragStart.y;
      let { x, y, w, h } = initialSelection;

      if (handle.includes("e")) w = Math.max(20, initialSelection.w + dx);
      if (handle.includes("s")) h = Math.max(20, initialSelection.h + dy);
      if (handle.includes("w")) {
        const newW = Math.max(20, initialSelection.w - dx);
        x = initialSelection.x + (initialSelection.w - newW);
        w = newW;
      }
      if (handle.includes("n")) {
        const newH = Math.max(20, initialSelection.h - dy);
        y = initialSelection.y + (initialSelection.h - newH);
        h = newH;
      }
      setSelection({ x, y, w, h });
    }
  };

  const handleMouseUp = () => {
    if (dragMode === "draw" && currentAnnotation) {
      setAnnotations((prev) => [...prev, currentAnnotation]);
      setCurrentAnnotation(null);
    }
    setDragMode(null);
  };

  // ── Render Final Cropped Canvas (Screen background + Annotations) ──
  const buildFinalCroppedCanvas = async () => {
    if (!selection || selection.w <= 10 || selection.h <= 10) return null;

    const dpr = window.devicePixelRatio || 1;
    const cropCanvas = document.createElement("canvas");
    cropCanvas.width = Math.round(selection.w * dpr);
    cropCanvas.height = Math.round(selection.h * dpr);
    const ctx = cropCanvas.getContext("2d");
    ctx.scale(dpr, dpr);

    // 1. Draw screen snapshot background if captured
    if (screenSnapshot) {
      const scaleX = screenSnapshot.width / window.innerWidth;
      const scaleY = screenSnapshot.height / window.innerHeight;
      ctx.drawImage(
        screenSnapshot,
        selection.x * scaleX,
        selection.y * scaleY,
        selection.w * scaleX,
        selection.h * scaleY,
        0,
        0,
        selection.w,
        selection.h
      );
    } else {
      ctx.fillStyle = "#0f0f1a";
      ctx.fillRect(0, 0, selection.w, selection.h);
    }

    // 2. Draw annotations offset to local selection coordinates
    ctx.save();
    ctx.translate(-selection.x, -selection.y);

    const renderAnnotation = (ann) => {
      ctx.save();
      ctx.strokeStyle = ann.color;
      ctx.fillStyle = ann.color;
      ctx.lineWidth = ann.width;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      switch (ann.type) {
        case "pen":
          if (ann.points && ann.points.length > 0) {
            ctx.beginPath();
            ctx.moveTo(ann.points[0].x, ann.points[0].y);
            for (let i = 1; i < ann.points.length; i++) ctx.lineTo(ann.points[i].x, ann.points[i].y);
            ctx.stroke();
          }
          break;

        case "highlighter":
          if (ann.points && ann.points.length > 0) {
            ctx.save();
            ctx.globalAlpha = 0.35;
            ctx.lineWidth = ann.width * 3;
            ctx.beginPath();
            ctx.moveTo(ann.points[0].x, ann.points[0].y);
            for (let i = 1; i < ann.points.length; i++) ctx.lineTo(ann.points[i].x, ann.points[i].y);
            ctx.stroke();
            ctx.restore();
          }
          break;

        case "line":
          ctx.beginPath();
          ctx.moveTo(ann.startX, ann.startY);
          ctx.lineTo(ann.endX, ann.endY);
          ctx.stroke();
          break;

        case "arrow":
          const dx = ann.endX - ann.startX;
          const dy = ann.endY - ann.startY;
          const angle = Math.atan2(dy, dx);
          const headLen = Math.max(12, ann.width * 3);

          ctx.beginPath();
          ctx.moveTo(ann.startX, ann.startY);
          ctx.lineTo(ann.endX, ann.endY);
          ctx.stroke();

          ctx.beginPath();
          ctx.moveTo(ann.endX, ann.endY);
          ctx.lineTo(ann.endX - headLen * Math.cos(angle - Math.PI / 6), ann.endY - headLen * Math.sin(angle - Math.PI / 6));
          ctx.lineTo(ann.endX - headLen * Math.cos(angle + Math.PI / 6), ann.endY - headLen * Math.sin(angle + Math.PI / 6));
          ctx.closePath();
          ctx.fill();
          break;

        case "rect":
          const rx = Math.min(ann.startX, ann.endX);
          const ry = Math.min(ann.startY, ann.endY);
          const rw = Math.abs(ann.endX - ann.startX);
          const rh = Math.abs(ann.endY - ann.startY);
          ctx.strokeRect(rx, ry, rw, rh);
          break;

        case "circle":
          const cx = (ann.startX + ann.endX) / 2;
          const cy = (ann.startY + ann.endY) / 2;
          const radiusX = Math.abs(ann.endX - ann.startX) / 2;
          const radiusY = Math.abs(ann.endY - ann.startY) / 2;
          ctx.beginPath();
          ctx.ellipse(cx, cy, radiusX, radiusY, 0, 0, 2 * Math.PI);
          ctx.stroke();
          break;

        case "step":
          ctx.beginPath();
          ctx.arc(ann.x, ann.y, 14, 0, 2 * Math.PI);
          ctx.fill();
          ctx.fillStyle = "#ffffff";
          ctx.font = "bold 13px sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(ann.stepNum.toString(), ann.x, ann.y);
          break;

        case "text":
          ctx.font = `bold ${Math.max(14, ann.width * 4)}px sans-serif`;
          ctx.textAlign = "left";
          ctx.textBaseline = "top";
          ctx.fillText(ann.text, ann.x, ann.y);
          break;

        case "blur":
          const bx = Math.min(ann.startX, ann.endX);
          const by = Math.min(ann.startY, ann.endY);
          const bw = Math.abs(ann.endX - ann.startX);
          const bh = Math.abs(ann.endY - ann.startY);
          ctx.save();
          ctx.fillStyle = "rgba(15, 15, 26, 0.95)";
          ctx.fillRect(bx, by, bw, bh);
          ctx.restore();
          break;

        default:
          break;
      }
      ctx.restore();
    };

    annotations.forEach(renderAnnotation);
    ctx.restore();

    return new Promise((resolve) => cropCanvas.toBlob((b) => resolve(b), "image/png"));
  };

  // Actions
  const handleSave = async () => {
    setIsProcessing(true);
    try {
      const blob = await buildFinalCroppedCanvas();
      if (!blob) {
        toast.error("Veuillez sélectionner une zone à capturer");
        setIsProcessing(false);
        return;
      }

      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
      const filename = `floomby_capture_${timestamp}.png`;
      const sizeKB = (blob.size / 1024).toFixed(0);

      if (window.showSaveFilePicker) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{ description: "Image PNG", accept: { "image/png": [".png"] } }],
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          toast.success(`💾 Floomby : Capture enregistrée (${handle.name} - ${sizeKB} KB)`);
          onClose();
          return;
        } catch (err) {
          if (err.name === "AbortError") {
            setIsProcessing(false);
            return;
          }
        }
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`💾 Floomby : Capture téléchargée (${filename} - ${sizeKB} KB)`);
      onClose();
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCopy = async () => {
    setIsProcessing(true);
    try {
      const blob = await buildFinalCroppedCanvas();
      if (!blob) {
        toast.error("Veuillez sélectionner une zone à capturer");
        setIsProcessing(false);
        return;
      }

      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
        toast.success("📋 Floomby : Image et annotations copiées dans le presse-papier !");
        onClose();
      } else {
        toast.error("Presse-papier d'images non supporté sur ce navigateur");
      }
    } catch {
      toast.error("Erreur lors de la copie");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleShare = async () => {
    setIsProcessing(true);
    try {
      const blob = await buildFinalCroppedCanvas();
      if (!blob) return;

      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
        toast.success("🌐 Floomby Cloud : Lien et image copiés avec succès !");
        onClose();
      }
    } catch {} finally {
      setIsProcessing(false);
    }
  };

  const handleUndo = () => {
    setAnnotations((prev) => prev.slice(0, -1));
  };

  if (!isActive) return null;

  const hasSelection = selection && selection.w > 10 && selection.h > 10;

  return (
    <div
      ref={containerOverlayRef}
      className="fixed inset-0 z-[10000] select-none overflow-hidden"
      style={{ cursor: activeTool === "select" ? "crosshair" : "crosshair" }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {/* ── Background Screen Snapshot Canvas ── */}
      {screenSnapshot && (
        <img
          src={screenSnapshot.toDataURL()}
          alt="Screen capture"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        />
      )}

      {/* ── Dark Semi-Transparent SVG Mask (Cutout inside selection) ── */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none">
        <defs>
          <mask id="floomby-mask">
            <rect width="100%" height="100%" fill="white" />
            {hasSelection && (
              <rect x={selection.x} y={selection.y} width={selection.w} height={selection.h} fill="black" rx="4" />
            )}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(0, 0, 0, 0.55)" mask="url(#floomby-mask)" />
      </svg>

      {/* ── Annotations Rendering Canvas ── */}
      <canvas ref={annotationCanvasRef} className="absolute inset-0 w-full h-full pointer-events-none z-10" />

      {/* ── Text Input Overlay ── */}
      {textInput && (
        <div
          className="absolute z-30"
          style={{ left: textInput.x, top: textInput.y }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <input
            type="text"
            autoFocus
            value={textInput.text}
            onChange={(e) => setTextInput({ ...textInput, text: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter" && textInput.text.trim()) {
                setAnnotations((prev) => [
                  ...prev,
                  { type: "text", x: textInput.x, y: textInput.y, text: textInput.text, color: activeColor, width: strokeWidth },
                ]);
                setTextInput(null);
              }
            }}
            placeholder="Tapez votre texte…"
            className="px-2 py-1 bg-black/90 text-white font-bold rounded border-2 border-cyan-400 outline-none text-sm shadow-xl"
            style={{ color: activeColor }}
          />
        </div>
      )}

      {/* ── Selection Box Border & Resize Handles ── */}
      {hasSelection && (
        <div
          className="absolute border-2 border-cyan-400 pointer-events-none shadow-[0_0_20px_rgba(6,182,212,0.5)] rounded"
          style={{ left: selection.x, top: selection.y, width: selection.w, height: selection.h }}
        >
          {/* Dimension Badge */}
          <div className="absolute -top-7 left-0 px-2 py-0.5 rounded bg-cyan-500 text-black font-mono text-[10px] font-bold shadow-md">
            {Math.round(selection.w)} × {Math.round(selection.h)} px
          </div>

          {/* 8 Drag Handles */}
          {["nw", "ne", "se", "sw", "n", "e", "s", "w"].map((h) => {
            const styles = {
              nw: "top-0 left-0 -translate-x-1/2 -translate-y-1/2 cursor-nwse-resize",
              ne: "top-0 right-0 translate-x-1/2 -translate-y-1/2 cursor-nesw-resize",
              se: "bottom-0 right-0 translate-x-1/2 translate-y-1/2 cursor-nwse-resize",
              sw: "bottom-0 left-0 -translate-x-1/2 translate-y-1/2 cursor-nesw-resize",
              n: "top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 cursor-ns-resize",
              e: "top-1/2 right-0 translate-x-1/2 -translate-y-1/2 cursor-ew-resize",
              s: "bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 cursor-ns-resize",
              w: "top-1/2 left-0 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize",
            };
            return (
              <div
                key={h}
                className={`absolute w-3 h-3 bg-white border-2 border-cyan-500 rounded-full shadow-md pointer-events-auto ${styles[h]}`}
              />
            );
          })}
        </div>
      )}

      {/* ── Floomby Top Hint Banner (when no box drawn) ── */}
      {!hasSelection && (
        <div className="absolute top-6 left-1/2 -translate-x-1/2 z-20 px-5 py-2.5 rounded-2xl bg-black/85 text-white backdrop-blur-md text-xs font-semibold border border-cyan-500/30 shadow-2xl flex items-center gap-2 pointer-events-none animate-bounce">
          <Crop className="h-4 w-4 text-cyan-400" />
          <span>SÉLECTIONNER UNE ZONE À CAPTURER (FLOOMBY PRO) · ÉCHAP POUR ANNULER</span>
        </div>
      )}

      {/* ── FLOOMBY VERTICAL DRAWING TOOLBAR (Right Side) ── */}
      {hasSelection && (
        <div
          className="absolute z-30 flex flex-col items-center gap-1.5 p-2 rounded-2xl bg-[#121222]/95 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/80 pointer-events-auto"
          style={{
            left: selection.x + selection.w + 14 > window.innerWidth - 60
              ? Math.max(10, selection.x - 56)
              : selection.x + selection.w + 14,
            top: Math.max(10, Math.min(window.innerHeight - 380, selection.y)),
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Tool buttons */}
          {[
            { id: "select", icon: MousePointer, label: "Sélection / Déplacement" },
            { id: "pen", icon: Pencil, label: "Crayon" },
            { id: "arrow", icon: ArrowRight, label: "Flèche" },
            { id: "rect", icon: Square, label: "Rectangle" },
            { id: "circle", icon: CircleIcon, label: "Cercle" },
            { id: "line", icon: Minus, label: "Ligne" },
            { id: "highlighter", icon: Highlighter, label: "Surligneur" },
            { id: "type", icon: Type, label: "Texte", toolId: "text" },
            { id: "step", icon: Hash, label: "Numérotation (1, 2, 3...)" },
            { id: "blur", icon: EyeOff, label: "Masquage / Flou" },
          ].map((t) => {
            const toolId = t.toolId || t.id;
            const Icon = t.icon;
            const isSelected = activeTool === toolId;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTool(toolId)}
                className={`p-2 rounded-xl transition-all ${
                  isSelected
                    ? "bg-cyan-500 text-black shadow-lg shadow-cyan-500/30 scale-105"
                    : "text-white/50 hover:text-white hover:bg-white/10"
                }`}
                title={t.label}
              >
                <Icon className="h-4 w-4" />
              </button>
            );
          })}

          <div className="w-6 h-px bg-white/10 my-1" />

          {/* Color palette */}
          <div className="grid grid-cols-2 gap-1">
            {COLOR_SWATCHES.map((c) => (
              <button
                key={c.hex}
                onClick={() => setActiveColor(c.hex)}
                className={`w-4 h-4 rounded-full transition-transform ${
                  activeColor === c.hex ? "scale-125 ring-2 ring-white ring-offset-1 ring-offset-[#121222]" : "hover:scale-110"
                }`}
                style={{ backgroundColor: c.hex }}
                title={c.name}
              />
            ))}
          </div>

          <div className="w-6 h-px bg-white/10 my-1" />

          {/* Stroke thickness */}
          <div className="flex flex-col gap-1 items-center">
            {STROKE_WIDTHS.map((w) => (
              <button
                key={w}
                onClick={() => setStrokeWidth(w)}
                className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${
                  strokeWidth === w ? "bg-white/20 text-cyan-400 font-bold" : "text-white/40 hover:text-white"
                }`}
                title={`Épaisseur ${w}px`}
              >
                <div
                  className="rounded-full bg-current"
                  style={{ width: `${w * 1.5 + 2}px`, height: `${w * 1.5 + 2}px` }}
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── FLOOMBY HORIZONTAL ACTION BAR (Bottom of Selection) ── */}
      {hasSelection && (
        <div
          className="absolute z-30 flex items-center gap-1.5 p-1.5 rounded-2xl bg-[#121222]/95 backdrop-blur-xl border border-cyan-500/30 shadow-2xl shadow-black/80 pointer-events-auto"
          style={{
            left: Math.min(window.innerWidth - 320, Math.max(10, selection.x + selection.w - 300)),
            top: selection.y + selection.h + 12 > window.innerHeight - 60
              ? Math.max(10, selection.y - 50)
              : selection.y + selection.h + 12,
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Undo */}
          <button
            onClick={handleUndo}
            disabled={annotations.length === 0}
            className="p-2 rounded-xl text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-30 transition-colors"
            title="Annuler (Ctrl+Z)"
          >
            <RotateCcw className="h-4 w-4" />
          </button>

          <div className="w-px h-5 bg-white/10" />

          {/* Copy */}
          <button
            onClick={handleCopy}
            disabled={isProcessing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 transition-colors disabled:opacity-50"
            title="Copier l'image avec annotations dans le presse-papier"
          >
            {isProcessing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
            <span>Copier</span>
          </button>

          {/* Download / Save */}
          <button
            onClick={handleSave}
            disabled={isProcessing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 transition-colors disabled:opacity-50"
            title="Enregistrer au format PNG"
          >
            {isProcessing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            <span>Enregistrer</span>
          </button>

          {/* Share */}
          <button
            onClick={handleShare}
            disabled={isProcessing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 transition-colors disabled:opacity-50"
            title="Partager / Copier lien"
          >
            <Share2 className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Partager</span>
          </button>

          <div className="w-px h-5 bg-white/10" />

          {/* Close */}
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="p-2 rounded-xl text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-colors"
            title="Fermer (Échap)"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
